import { TemplatePlaceholder } from '@/types';

export type FormatDateRangeOptions = {
  /** Default false. If set to true will throw an error if the provided date is
   * not zeroed out (set  midnight in the local timezone) */
  confirmZeroedTimestamp?: boolean;
};

/**
 * Converts a Date object or a placeholder to a ISO8601 string representing a
 * date (not a timestamp).
 *
 * Motivation: For use when mapping part of a date range in a widget value to a
 * filter value. E.g from `[EventAttendanceWidgetValue['eventDateRange']` to
 * `EventAttendanceFilter['eventDateRange']`.
 *
 * If the date is not an actual date but a placeholder for a date, as identified
 * by the provided isTemplatePlaceholder guard, it will left unchanged.
 *
 * It is expected that the original timestamp of the Date will be zeroed to
 * midnight. This can be explicitly checked for by using the
 * confirmZeroedTimestamp option which is false by default.
 *
 * @param date - the date, placeholder or null value indicating not set
 * @param isTemplatePlaceholder - guard for the placeholder type
 * @param valueIfNull - value to return if date is null
 * @param options - options
 * @returns ISO8601 string or placeholder or valueIfNull
 */
export function formatTemplateCapableDateForFilterValue<ValueIfNull extends undefined | null>(
  date: Date | null | TemplatePlaceholder,
  isTemplatePlaceholder: (value: unknown) => value is TemplatePlaceholder,
  valueIfNull: ValueIfNull,
  options: FormatDateRangeOptions = {}
): ValueIfNull | string | TemplatePlaceholder {
  if (isTemplatePlaceholder(date)) return date;

  return formatDateForFilterValue(date, valueIfNull, options);
}

/**
 * Converts a Date object or a placeholder to a ISO8601 string representing a
 * date (not a timestamp).
 *
 * Motivation: For use when mapping part of a date range in a widget value to a
 * filter value. E.g from `[EventAttendanceWidgetValue['eventDateRange']` to
 * `EventAttendanceFilter['eventDateRange']`.
 *
 * It is expected that the original timestamp of the Date will be zeroed to
 * midnight. This can be explicitly checked for by using the
 * confirmZeroedTimestamp option which is false by default.
 *
 * @param date - the date, placeholder or null value indicating not set
 * @param valueIfNull - value to return if date is null
 * @param options - options
 * @returns ISO8601 string or valueIfNull
 */
export function formatDateForFilterValue<ValueIfNull extends undefined | null>(
  date: Date | null,
  valueIfNull: ValueIfNull,
  options: FormatDateRangeOptions = {}
): ValueIfNull | string {
  const { confirmZeroedTimestamp = false } = options;

  if (!date) return valueIfNull;

  if (confirmZeroedTimestamp && !isTimeAtMidnight(date)) {
    throw new TypeError('Date must have its time component zeroed out (set to midnight).');
  }

  return formatToYYYYMMDD(date);
}

function formatToYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function isTimeAtMidnight(date: Date): boolean {
  return (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}
